import {isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {maxLeftPanelWidth} from '../../../../libs/shared/ui/src/components/leftPanel/leftPanelHelpers';
import {LiveDashboardContentModel} from '../../../models/api/liveDashboardContentModel';
import {renderDurationValue} from '../../helpers/metricRenderers/durationMetricRenderer';
import {renderNumericalValue} from '../../helpers/metricRenderers/numericalMetricRenderer';
import {ReportElementIdsEnum} from '../../helpers/reportHelpers/reportTypes';
import {KeyMetricValue, KeyMetricValueSkeleton} from '../reportElements/keyMetrics/keyMetricValue';
import {AnalyticsEducationTooltip} from '../reportElements/tooltips/analyticsTooltips';
import {
  LIVE_DASHBOARD_2_BY_2_HEADLINE_METRICS_BREAKPOINT_VIEWPORT_WIDTH,
  LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
} from './constants';
import {MetricSection} from './metricSection';

/*
 * Props.
 */

interface LiveDashboardHeadlineMetricsProps {
  selectedInboxIds: ReadonlyArray<number>;
  hasWaitingMetrics: boolean;
  liveDashboardContent: LiveDashboardContentModel | undefined;
  isThisPoppedOut: boolean;
}

/*
  Styles.
*/

// The parent grid (`LiveDashboardContent`) has 12 columns.
const StyledMetricSection = styled(MetricSection)<{
  $hasWaitingCount: boolean;
  $twoByTwoLayoutBreakpoint: number;
}>`
  grid-column: span ${({$hasWaitingCount}) => ($hasWaitingCount ? 3 : 4)};

  /* On smaller screen widths and when showing the Waiting metric, place metrics in a 2x2 square. */
  @media screen and (max-width: ${({$twoByTwoLayoutBreakpoint}) => $twoByTwoLayoutBreakpoint}px) {
    grid-column: span ${({$hasWaitingCount}) => ($hasWaitingCount ? 6 : 4)};
  }
`;

/*
 * Component.
 */

export const LiveDashboardHeadlineMetrics: FC<LiveDashboardHeadlineMetricsProps> = ({
  hasWaitingMetrics,
  liveDashboardContent,
  isThisPoppedOut,
}) => {
  const isLoading = isUndefined(liveDashboardContent);
  const {openCount, unassignedCount, oldestUnrepliedTimestamp, waitingCount} = liveDashboardContent ?? {};

  const oldestUnrepliedDurationSeconds = oldestUnrepliedTimestamp
    ? (new Date().getTime() - oldestUnrepliedTimestamp) / 1_000
    : 0;
  const oldestUnrepliedDuration = renderDurationValue({
    value: oldestUnrepliedDurationSeconds,
    isCompact: true,
  });

  const twoByTwoLayoutBreakpoint = maybeAddP1WidthToBreakpoint(
    LIVE_DASHBOARD_2_BY_2_HEADLINE_METRICS_BREAKPOINT_VIEWPORT_WIDTH,
    isThisPoppedOut,
  );

  const renderMetric = (
    sectionId: ReportElementIdsEnum,
    tooltip: AnalyticsEducationTooltip,
    value: ReactNode,
  ) => (
    <StyledMetricSection
      reportSectionId={sectionId}
      label={tooltip.title}
      tooltip={tooltip}
      $hasWaitingCount={hasWaitingMetrics}
      $twoByTwoLayoutBreakpoint={twoByTwoLayoutBreakpoint}
    >
      {isLoading ? <KeyMetricValueSkeleton /> : <KeyMetricValue>{value}</KeyMetricValue>}
    </StyledMetricSection>
  );

  return (
    <>
      {renderMetric(
        ReportElementIdsEnum.LIVE_DASHBOARD_OPEN_COUNT,
        openMetricTooltip,
        renderNumericalValue({value: openCount ?? 0, isZeroValid: true}),
      )}
      {hasWaitingMetrics &&
        renderMetric(
          ReportElementIdsEnum.LIVE_DASHBOARD_WAITING_COUNT,
          waitingMetricTooltip,
          renderNumericalValue({value: waitingCount ?? 0, isZeroValid: true}) ?? '-',
        )}
      {renderMetric(
        ReportElementIdsEnum.LIVE_DASHBOARD_UNASSIGNED_COUNT,
        hasWaitingMetrics ? unassignedMetricTooltipWithWaiting : unassignedMetricTooltip,
        renderNumericalValue({value: unassignedCount ?? 0, isZeroValid: true}),
      )}
      {renderMetric(
        ReportElementIdsEnum.LIVE_DASHBOARD_OLDEST_OPEN_DELAY,
        hasWaitingMetrics ? oldestMetricTooltipWithWaiting : oldestOpenMetricTooltip,
        oldestUnrepliedDuration,
      )}
    </>
  );
};

/*
 * Translatable strings.
 */

const openMetricTooltip: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-open-metric-title">Open</Trans>,
  tooltipContent: (
    <Trans id="live-dashboard-open-metric-content">
      Track the volume of conversations your team has to work on.
    </Trans>
  ),
  calculationExplanation: (
    <Trans id="live-dashboard-open-metric-explanation">
      Total number of conversations currently open, including assigned and unassigned.
    </Trans>
  ),
  learnMoreUrl: LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
};

const waitingMetricTooltip: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-waiting-metric-title">Waiting</Trans>,
  tooltipContent: (
    <Trans id="live-dashboard-waiting-metric-content">
      Track the volume of conversations that are not yet resolved, but no action is needed for now.
    </Trans>
  ),
  calculationExplanation: (
    <Trans id="live-dashboard-waiting-metric-explanation">
      Total number of conversations currently waiting or snoozed, including assigned and unassigned.
    </Trans>
  ),
  learnMoreUrl: LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
};

const unassignedMetricTooltip: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-unassigned-metric-title">Unassigned</Trans>,
  tooltipContent: (
    <Trans id="live-dashboard-unassigned-metric-content">
      Track the volume of conversations that haven’t been assigned yet.
    </Trans>
  ),
  calculationExplanation: (
    <Trans id="live-dashboard-unassigned-metric-explanation">
      Total number of conversations currently open and unassigned.
    </Trans>
  ),
  learnMoreUrl: LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
};

// TODO(ldemarne): Figure out if we can use this tooltip in every situation.
const unassignedMetricTooltipWithWaiting: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-unassigned-metric-title">Unassigned open</Trans>,
  tooltipContent: (
    <Trans id="live-dashboard-unassigned-metric-content">
      Track the volume of conversations that haven’t been assigned yet.
    </Trans>
  ),
  calculationExplanation: (
    <Trans id="live-dashboard-unassigned-metric-explanation">
      Total number of conversations currently open and unassigned.
    </Trans>
  ),
  learnMoreUrl: LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
};

const oldestOpenMetricTooltip: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-oldest-open-metric-title">Oldest open</Trans>,
  tooltipContent: (
    <Trans id="live-dashboard-oldest-open-metric-content">Identify the oldest conversation.</Trans>
  ),
  calculationExplanation: (
    <Trans id="live-dashboard-oldest-open-metric-explanation">
      Longest time an open conversation has been without any new messages.
    </Trans>
  ),
  learnMoreUrl: LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
};

// TODO(ldemarne): Figure out if we can use this tooltip in every situation.
const oldestMetricTooltipWithWaiting: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-oldest-metric-title">Oldest</Trans>,
  tooltipContent: <Trans id="live-dashboard-oldest-metric-content">Identify the oldest conversation.</Trans>,
  calculationExplanation: (
    <Trans id="live-dashboard-oldest-metric-explanation">
      Longest time a conversation has been without any new messages.
    </Trans>
  ),
  learnMoreUrl: LIVE_DASHBOARD_METRICS_HELP_CENTER_URL,
};

/*
 * Helpers.
 */

function maybeAddP1WidthToBreakpoint(breakpointValue: number, isThisPoppedOut: boolean) {
  if (isThisPoppedOut) {
    return breakpointValue;
  }

  return breakpointValue + maxLeftPanelWidth;
}
